import './RouteDetailsForm.scss';
import * as R from 'ramda';
import { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { Button } from '@Components/Button/Button';
import { Size } from '@Components/designConstants';
import { useSetError } from '../../../redux/modals';
import { WtoLambdaFetch } from '../../../utils/fetch-utils';
import { CancelModal } from '../Modals/CancelModal';
import { ValidationErrors } from '../Validation/ValidationErrors';
import { formValidator } from '../Validation/wto-form-validator';
import { WtoTab, WtoTabType } from '../web-traffic-optimization-constants';
import { FormContext, formObjDefault, WtoVariant } from '../wto-form-context';
import {
  buildPloverUrl,
  deriveFormEdited,
  formatDefaultId,
  formatRules,
  isSavedDraft,
  trimBackslashes
} from '../wto-utils';
import { FallbackVariantSection } from './FallbackVariantSection';
import { RouteUrlBuilder } from './RouteUrlBuilder';
import { TrackingUrlParameterSection } from './TrackingUrlParameterSection';
import { VariantsSection } from './VariantsSection';

interface RouteDetailsFormProps {
  domains: { web_url: string }[];
  goToTab: (tab: WtoTabType) => void;
  readOnly: boolean;
  setReadOnly: Dispatch<SetStateAction<boolean>>;
  formEdited: boolean;
  setFormEdited: Dispatch<SetStateAction<boolean>>;
  cancelConfirmation?: WtoTabType;
  setCancelConfirmation: Dispatch<SetStateAction<WtoTabType | undefined>>;
  userEmail: string;
  fetchPlovers: () => void;
  companyId: string;
}

export const RouteDetailsForm = ({
  domains = [],
  goToTab,
  readOnly,
  setReadOnly,
  formEdited,
  setFormEdited,
  cancelConfirmation,
  setCancelConfirmation,
  userEmail,
  fetchPlovers,
  companyId
}: RouteDetailsFormProps) => {
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialVariants, setInitialVariants] = useState<WtoVariant[]>([]);

  const { wtoFormObj, setWtoFormObj } = useContext(FormContext);
  const setError = useSetError();

  const ploverUrl = buildPloverUrl(
    wtoFormObj.domain,
    wtoFormObj.folder_path,
    wtoFormObj.plover_url
  );

  useEffect(() => {
    if (initialVariants.length === 0) {
      setInitialVariants(wtoFormObj.variants);
    }
    const edited = deriveFormEdited(wtoFormObj);
    setFormEdited(edited);
    setReadOnly(!(wtoFormObj.status.toLocaleLowerCase() === 'draft' || wtoFormObj.status === ''));
  }, [wtoFormObj, setFormEdited, setReadOnly, initialVariants]);

  const validateForm = (draft?: boolean) => {
    let errors = formValidator(
      wtoFormObj,
      [
        'test_name',
        'domain',
        'folder_path',
        'plover_url',
        'utm_test_id',
        'variants',
        'default_id',
        'default_url'
      ],
      {
        isBulkPlover: false
      }
    );
    wtoFormObj.variants.forEach((currVariant, currIndex) => {
      const sameIdIndex = wtoFormObj.variants.findIndex(
        (variant: { variant_id: string; split: number }, index: number) =>
          index !== currIndex && variant.variant_id === currVariant.variant_id
      );
      const fDashIndex = currVariant.variant_id.indexOf('f-');
      if (sameIdIndex !== -1) {
        errors.push(
          `Multiple variants with ID '${currVariant.variant_id}' detected; variant IDs must be unique`
        );
      }
      if (fDashIndex === 0) {
        errors.push(
          `Variant ID '${currVariant.variant_id}' includes the prefix 'f-'; this prefix is reserved for fallback variant IDs`
        );
      }
    });
    errors = [...new Set(errors)];
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }
    if (draft) {
      saveAsDraft();
      return;
    }
    if (!R.equals(wtoFormObj.variants, initialVariants)) {
      const newRules = R.clone(wtoFormObj.rules);
      wtoFormObj.rules.forEach((rule, ruleIndex) => {
        const newRule = R.clone(rule);
        newRule.additional_utm = [];
        newRule.cookie_operation = [];
        newRule.cookie_operation_url = [];
        newRule.dow_operation = [];
        newRules[ruleIndex] = newRule;
      });
      updateFormField('rules', newRules);
    }
    goToTab(WtoTab.ROUTE_RULES);
  };

  const modifyVariantInRules = (oldVariant: WtoVariant, newVariant: WtoVariant) => {
    const newRules = R.clone(wtoFormObj.rules);
    wtoFormObj.rules.forEach((rule, ruleIndex) => {
      const newRule = R.clone(rule);
      const variantIndex = rule.variant_splits.findIndex(
        (variant: { variant_id: string; split: number }) =>
          variant.variant_id === oldVariant.variant_id
      );

      const newVariants = R.clone(rule.variant_splits);
      if (variantIndex !== -1) {
        newVariants[variantIndex] = {
          variant_id: newVariant.variant_id,
          split: rule.variant_splits[variantIndex].split
        };
      }
      newRule.variant_splits = newVariants;
      newRules[ruleIndex] = newRule;
    });
    return newRules;
  };

  const onVariantChange = (index: number, updatedVariant: WtoVariant) => {
    const updatedVariants = [...wtoFormObj.variants];
    updatedVariants[index] = updatedVariant;
    const updatedRules = modifyVariantInRules(wtoFormObj.variants[index], updatedVariant);
    updateFormField('variants', updatedVariants);
    updateFormField('rules', updatedRules);
  };

  const onVariantDelete = (index: number) => {
    const variantInUse = wtoFormObj.rules.some(rule =>
      rule.variant_splits.some(
        (split: { split: string; variant_id: string }) =>
          split.variant_id === wtoFormObj.variants[index].variant_id
      )
    );
    if (variantInUse) {
      setValidationErrors([
        "This variant can't be deleted because it is being used within a traffic split for a rule."
      ]);
    } else {
      setValidationErrors([]);
      updateFormField('variants', wtoFormObj.variants.toSpliced(index, 1));
    }
  };

  const confirmCancel = () => {
    setWtoFormObj({ ...formObjDefault });
    setCancelConfirmation(undefined);
    goToTab(cancelConfirmation || WtoTab.ROUTE_TABLE);
  };

  const onCancelClick = () => {
    if (formEdited && !readOnly) {
      setCancelConfirmation(WtoTab.ROUTE_TABLE);
    } else {
      confirmCancel();
    }
  };

  const updateFormField = (key: string, value: unknown) =>
    setWtoFormObj(current => ({ ...current, [key]: value }));

  const saveAsDraft = async () => {
    setLoading(true);
    try {
      await WtoLambdaFetch(`/plover${isSavedDraft(wtoFormObj) ? `/${wtoFormObj.id}` : ''}`, {
        method: isSavedDraft(wtoFormObj) ? 'PATCH' : 'POST',
        params: {
          email: userEmail
        },
        body: {
          company: companyId,
          test_name: wtoFormObj.test_name,
          utm_test_id: wtoFormObj.utm_test_id,
          default_url: wtoFormObj.default_url,
          default_id: formatDefaultId(wtoFormObj.default_id),
          plover_url: wtoFormObj.plover_url,
          folder_path: trimBackslashes(wtoFormObj.folder_path),
          notes: wtoFormObj.notes,
          variants: wtoFormObj.variants,
          domain: wtoFormObj.domain,
          rules: formatRules(wtoFormObj.rules),
          status: 'draft'
        }
      });
      fetchPlovers();
      goToTab(WtoTab.ROUTE_TABLE);
      setCancelConfirmation(undefined);
    } catch (e) {
      setError({
        message: `Failed to save draft. Error: ${e.message}`,
        reportError: e
      });
    }
    setLoading(false);
  };

  return (
    <div className='route-details-form-container' data-testid='route-details-form'>
      <Form className='route-details-form'>
        <ValidationErrors validationErrors={validationErrors} />
        <Row className='align-items-end'>
          <Form.Group as={Col} className='form-group'>
            <Form.Label>Route Name</Form.Label>
            <Form.Control
              className={readOnly ? 'disable' : 'enable'}
              value={wtoFormObj.test_name || ''}
              disabled={readOnly}
              placeholder='Route Name. e.g. Quiz CTA Test'
              onChange={e => updateFormField('test_name', e.target.value)}
            />
          </Form.Group>
        </Row>
        <Row>
          <Form.Group as={Col} className='form-group'>
            <Form.Label>Route URL</Form.Label>
            <div className='helper-text'>
              <p>
                Please select Domain Name and enter Route Name below. These would be used to
                construct the following Route URL:
              </p>
              <RouteUrlBuilder
                ploverUrl={ploverUrl}
                readOnly={readOnly}
                domain={wtoFormObj.domain}
                folderPath={wtoFormObj.folder_path}
                ploverName={wtoFormObj.plover_url}
                domains={domains}
                onDomainChange={domain => updateFormField('domain', domain)}
                onFolderPathChange={path => updateFormField('folder_path', path)}
                onPloverNameChange={name => updateFormField('plover_url', name)}
              />
            </div>
          </Form.Group>
        </Row>

        <TrackingUrlParameterSection
          utmTestId={wtoFormObj.utm_test_id}
          readOnly={readOnly}
          onChange={value => updateFormField('utm_test_id', value)}
        />

        <VariantsSection
          variants={wtoFormObj.variants}
          readOnly={readOnly}
          onVariantChange={onVariantChange}
          onVariantDelete={onVariantDelete}
          onAddVariant={() =>
            updateFormField('variants', [
              ...wtoFormObj.variants,
              { variant_id: '', variant_url: '' }
            ])
          }
        />

        <FallbackVariantSection
          defaultId={wtoFormObj.default_id}
          defaultUrl={wtoFormObj.default_url}
          readOnly={readOnly}
          onDefaultIdChange={value => updateFormField('default_id', value)}
          onDefaultUrlChange={value => updateFormField('default_url', value)}
        />

        <Row>
          <Form.Group as={Col} className='form-group'>
            <Form.Label>Notes (optional)</Form.Label>
            <Form.Control
              disabled={readOnly}
              as='textarea'
              rows={3}
              className='notes'
              type='text'
              placeholder='Please enter your split description and split goals here.'
              value={wtoFormObj.notes || ''}
              onChange={e => updateFormField('notes', e.target.value)}
            />
          </Form.Group>
        </Row>
      </Form>
      <div className='form-buttons'>
        <Button size={Size.LG} onClick={onCancelClick}>
          Cancel
        </Button>
        <Button
          size={Size.LG}
          disabled={!formEdited || readOnly}
          loading={loading}
          onClick={() => validateForm(true)}
        >
          Save as Draft
        </Button>
        <Button
          size={Size.LG}
          disabled={loading}
          onClick={e => {
            e.preventDefault();
            if (wtoFormObj.status.toLocaleLowerCase() === 'draft') {
              validateForm();
            } else {
              goToTab(WtoTab.ROUTE_RULES);
            }
          }}
        >
          Continue to Rules
        </Button>
      </div>
      <CancelModal
        show={Boolean(cancelConfirmation)}
        closeModal={() => setCancelConfirmation(undefined)}
        confirmCancel={confirmCancel}
      />
    </div>
  );
};
